import * as z from 'zod/mini';
import { exposeOnWindow, secondsToTime } from './helpers';
import { getConsoleLogPrefix } from './pretty-logs';
import { setSentryTag } from './sentry';

let logs: unknown[][] = [];

if (typeof window !== 'undefined') {
  // Should already initialized in index.html for debugging issues with the service worker
  window.debugLogs = window.debugLogs || [];
  logs = window.debugLogs; // Alias for brevity
}

const maxLogs = 500;

const coloredLogSchema = z.object({
  logName: z.string(),
  logColor: z.string(),
});

debugLog(
  { logName: 'startup', logColor: 'darkblue' },
  'Starting session',
  getCommitHash()
);

export default function debugLog(...args: unknown[]) {
  if (typeof window === 'undefined') return;

  let name = 'debug';
  let color = 'darkcyan';
  const coloredLogResult = coloredLogSchema.safeParse(args[0]);
  if (coloredLogResult.success) {
    name = coloredLogResult.data.logName;
    color = coloredLogResult.data.logColor;
    args.shift();
  }

  console.log(...getConsoleLogPrefix(name, color), ...args); // eslint-disable-line no-console
  if (name !== 'debug') {
    args.unshift(`[${name}]`);
  }
  logs.push([
    Date.now(),
    ...args.map((val) => {
      if (!val || typeof val !== 'object') return val;
      try {
        return JSON.stringify(val);
      } catch (error) {
        return String(val);
      }
    }),
  ]);

  // Keep up to 100 audio logs in store
  if (logs.length > maxLogs) {
    logs.splice(0, logs.length - maxLogs);
  }
}

export function getLogs(amount = maxLogs): any[][] {
  const logsToReturn = amount < logs.length ? logs.slice(logs.length - amount) : logs;
  if (!logsToReturn.length) return [];
  return (JSON.parse(JSON.stringify(logsToReturn)) as any[][]).map((log) => {
    // Start at time zero and log in hh:mm:ss format
    log[0] = getTimeYmdHms(log[0]);
    return log;
  });
}

export function getCommitHash() {
  return import.meta.env.VITE_COMMIT_HASH;
}
exposeOnWindow({
  currentCommitUrl:
    'https://github.com/micahjon/adblock-podcast/commit/' + getCommitHash(),
});

function getTimeYmdHms(timestamp: number) {
  const date = new Date(timestamp);

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  const hour = date.getHours();
  const minute = date.getMinutes();
  const second = date.getSeconds();
  const ms = date.getMilliseconds();

  const ymd = [year, month, day].map(zeroPad).join('-');
  const hms = [hour, minute, second].map(zeroPad).join(':');
  return `${ymd} ${hms}.${ms}`;
}

function zeroPad(x: number) {
  return x < 10 ? '0' + x : x;
}

if (typeof document !== 'undefined') {
  let lastChangeTime = Date.now();
  let priorState = document.visibilityState;
  setSentryTag('visibility', priorState);
  document.addEventListener('visibilitychange', () => {
    const seconds = Math.round((Date.now() - lastChangeTime) / 1000);
    const timeEllapsed = secondsToTime(seconds);
    const currentState = document.visibilityState;
    setSentryTag('visibility', currentState);
    debugLog(`Visibility Change: ${currentState} (${priorState} for ${timeEllapsed})`);
    if (currentState === 'visible') {
      dispatchEvent(
        new CustomEvent('APP_VISIBLE', {
          detail: { hiddenFor: seconds },
        })
      );
    }
    lastChangeTime = Date.now();
    priorState = currentState;
  });

  // // Debug stuff not working when PWA is in background (e.g. iPhone is locked)
  // let lastTickTime = Date.now();
  // const logTick = function () {
  //   log('tick', Date.now() - lastTickTime);
  //   lastTickTime = Date.now();
  //   setTimeout(logTick, 5000);
  // };
  // setTimeout(logTick, 5000);
}

exposeOnWindow({ getLogs });
